/**
 * Squat jump. Counting is measured off real footage; the criteria are not.
 *
 * It counts with the generic oscillation counter rather than the revolution
 * accumulator — the knee leaves a standing value, closes into the
 * counter-movement and opens again — and its criteria read the legs that the
 * swing's counter deliberately ignores. If a second exercise could be expressed
 * without touching the geometry, the sample, the readout or the receipt, the
 * exercise layer would not be worth having.
 *
 * Three things this drill taught, all off the take in
 * `fixtures/squat_jump/`; `docs/squat-jump.md` records the numbers.
 *
 * **The knee does not lock out between jumps.** At a 1.5s cadence the athlete
 * came back to 154-162 degrees and no further, so a counter asking for 160 — a
 * reasonable figure for a squat, and what this file asked for when it was one —
 * merged five of the ten gaps and counted 6 reps against 11 performed. The
 * threshold is 145 for that reason, which still leaves 40 degrees between it and
 * the bottom of the movement, far outside the 1-3 degree noise floor.
 *
 * **Heels down is a squat's criterion, not a jump's.** It failed all 11 reps at
 * 0.82-0.92 foot lengths, which is the push-off doing exactly what it should.
 * A jump wants the opposite, so what is checked is that the ankle extended.
 *
 * **The foot in the air is not measurable either, and that one was tested.**
 * The athlete asked for pointed toes, so this file judged the ankle extension
 * and called the row "Toes". Three takes say it cannot: a take performed with
 * the feet deliberately pulled towards the shin — the caricature of the fault,
 * filmed to settle this — reads 132-137 degrees of ankle angle in the air where
 * the good take reads 126-133. It moves the WRONG way, and the same holds on
 * the raw landmarks, so smoothing is not the cause. Within any one take the
 * spread is about 5 degrees while takes differ by 10, which is a measurement
 * reporting the camera and the pose prior rather than the foot; MediaPipe
 * reports 0.97-0.98 visibility on those landmarks throughout, which is what a
 * guess looks like from here. The push-off is real and physical, and it is what
 * the airborne window is built from, so the row stays as `informational` and is
 * labelled "Push-off". No cue claims anything about the toes.
 *
 * **Jump height is not measurable here.** `worldLandmarks` are hip-centred, so
 * the origin rises with the athlete and a body leaving the floor is the one
 * thing the coordinates cannot show. Ankle extension at the push is a proxy for
 * the effort, not a height, and is labelled as such.
 *
 * Geometry note: both criteria are sagittal, so this is a side-on drill like the
 * swing. Depth read from the front is foreshortened by however the athlete is
 * turned, which measures the camera angle rather than the squat.
 *
 * `calibrated: true` as of the four takes above, which is what the protocol in
 * `fixtures/README.md` asks for: a clean set, a set of deliberate faults, and
 * every surviving criterion shown both to pass the first and to catch something
 * in the second. One criterion did not survive it, which is the protocol
 * working rather than a reason to hold the flag back. What the flag does NOT
 * claim is a coaching standard: these are one athlete on one day, the thinnest
 * margin is `jumpLegsMin` at 4 degrees under his weakest clean rep, and
 * `docs/squat-jump.md` records every one of them.
 */
import { OscillationCounter } from "./oscillation";
import { footScale, facingSign } from "../metrics/posture";

function median(values) {
  if (values.length === 0) {
    return null;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * The airborne phase of one rep: from take-off — the frame the ankle is most
 * extended — for as long as it stays extended past `jumpAirToes`.
 *
 * This is not the same thing as the rep window, which runs through the landing
 * and into the athlete standing up. It is what says the feet left the floor at
 * all, which is why both the criteria and the counter read it: the criteria so
 * they judge the shape in the air rather than the shape of someone standing,
 * and the counter so that bending the knees on the floor is not a jump.
 *
 * @return `{ air, airMs, flight, knees, deepest, foot, peakToes }`, with `air`
 *   empty when there was no flight in the samples given.
 */
export function airborneWindow(samples, side, thresholds, scale = null) {
  const foot = scale || footScale(samples, side);
  const knees = samples.map((s) => s.knee[side]);
  const deepest = Math.min(...knees);
  // The jump is everything after the bottom. Measuring over the whole rep would
  // read the extension criteria off the descent, where the body is folded on
  // purpose and every one of them would fail.
  const flight = samples.slice(knees.indexOf(deepest));
  const toeRatios = flight.map((s) =>
    foot > 0 ? s.heelLift[side] / foot : null
  );
  const takeOff = toeRatios.indexOf(Math.max(...toeRatios));
  const air = [];
  for (let i = takeOff; i < flight.length; i += 1) {
    if (!(toeRatios[i] > thresholds.jumpAirToes)) {
      break;
    }
    air.push(flight[i]);
  }
  return {
    air,
    airMs: air.length > 0 ? air[air.length - 1].t - air[0].t : 0,
    flight,
    knees,
    deepest,
    foot,
    peakToes: Math.max(...flight.map((s) => s.heelLift[side])),
  };
}

export const squatJump = {
  id: "squat_jump",
  name: "Squat jump",
  blurb: "Load, then stretch out in the air",
  view: "side",
  calibrated: true,
  thresholdKeys: [
    "jumpKneeDown",
    "jumpKneeUp",
    "jumpFlightMs",
    "jumpDepthMax",
    "jumpLegsMin",
    "jumpHipsMin",
    "jumpAirToes",
    "jumpAirMinMs",
    "jumpTrunkMax",
    "jumpArmSwingMin",
    "neckOffset",
    "neckTolerance",
  ],
  cues: {
    jump_depth: "Load deeper",
    jump_legs: "Straighten your legs",
    jump_hips: "Open your hips",
    jump_trunk: "Stand tall in the air",
    jump_arms: "Swing your arms back",
    // "Head level" rather than anything about where to look: gaze is not
    // something the landmarks report.
    jump_head: "Head level",
  },
  needsFootScale: true,
  /**
   * Knee angle is the signal: near 180 standing, closing into the
   * counter-movement and opening through the jump. The two thresholds stay far
   * apart — below `jumpKneeDown`, then back above `jumpKneeUp` — so no wobble at
   * either end counts twice, but `jumpKneeUp` sits below a locked knee because
   * at speed the athlete never reaches one.
   *
   * The tail is what makes the flight part of the rep. `jumpKneeUp` has to sit
   * partway up the push for the rep to count at all, so without it the window
   * would close before the body finished extending and every criterion below
   * would be reading the push rather than the jump.
   */
  createCounter: (thresholds, { footScale: sessionScale = () => null } = {}) =>
    new OscillationCounter({
      signal: (sample, side) => sample.knee[side],
      low: thresholds.jumpKneeDown,
      high: thresholds.jumpKneeUp,
      tailMs: thresholds.jumpFlightMs,
      // The legs ARE the movement here, so unlike the swing the counter cannot
      // run on a take framed from the knees up: a knee the model is guessing at
      // crosses both thresholds on its own. On the reference take this is what
      // keeps the walk-in and the walk-out from counting as reps.
      visibility: (sample, side) => sample.legVisibility[side],
      minVisibility: thresholds.minVisibility,
      maxGapMs: thresholds.maxGapMs,
      // A jump is the feet leaving the floor, and the knee alone cannot say
      // whether they did. Bending the knees on the floor traces the same curve,
      // so an athlete settling himself between jumps — stepping back into
      // position, shaking out a leg — was counted as an attempt and told it was
      // a bad one. What makes it a rep is that it had flight in it.
      //
      // This is also why resting mid-set now costs nothing: a set is the reps
      // performed in it, not the time between them, and a drill this heavy is
      // one people stop in the middle of.
      accept: (samples, side) =>
        airborneWindow(samples, side, thresholds, sessionScale()).airMs >=
        thresholds.jumpAirMinMs,
    }),
  scoreRep: (samples, { side, thresholds, footScale: scale }) => {
    if (samples.length === 0) {
      return [];
    }
    const legsSeen =
      median(samples.map((s) => s.legVisibility[side])) >=
      thresholds.minVisibility;
    const {
      air,
      airMs,
      flight,
      knees,
      deepest: lowest,
      foot,
      peakToes,
    } = airborneWindow(samples, side, thresholds, scale);
    const deepest = legsSeen ? lowest : null;
    // A rep the counter accepted always has flight in it, but `scoreRep` is
    // also called straight from a script against arbitrary samples, so the
    // criteria below still report unjudged rather than grading a body that
    // never left the floor.
    const airborne =
      legsSeen && foot > 0 && airMs >= thresholds.jumpAirMinMs ? air : null;

    // Each figure is taken at its own best moment within that window rather
    // than all at one frame. The three do not peak together in this data — the
    // ankle extends hardest at take-off and the knee reads its maximum a third
    // of a second later, which is mostly the smoothing filter's lag rather than
    // the athlete — and a single frame would charge each criterion for the
    // others' timing. What this does NOT check is that the shape happened all
    // at once, which is what a diver is actually after; see docs/squat-jump.md.
    const peakKnee =
      airborne == null ? null : Math.max(...airborne.map((s) => s.knee[side]));
    // The hip is the other half of "not extended", and on that same take it is
    // the half that showed: 114-167 in the air against 163-178 clean, where the
    // knee overlapped. Measured separately from the knee so the cue can name
    // the joint that folded.
    const hips =
      airborne == null
        ? []
        : airborne.map((s) => s.hip[side]).filter((v) => Number.isFinite(v));
    const peakHip = hips.length > 0 ? Math.max(...hips) : null;
    const tallest =
      airborne == null
        ? null
        : Math.min(...airborne.map((s) => Math.abs(s.trunkTilt)));
    const toes = legsSeen && foot > 0 ? peakToes / foot : null;
    // Typical rather than worst, the way the swing reads it: the head moves
    // through a jump and one frame of it dipping is not a fault. Deviation from
    // the athlete's own neutral, so positive is chin down. Frames with no
    // reading are dropped rather than sorted among the numbers, and a flight
    // with none left reports unjudged instead of a fault.
    const pitches = flight
      .map((s) => (s.headPitch == null ? null : s.headPitch[side]))
      .filter((v) => Number.isFinite(v));
    const headPitch = median(pitches);
    const head = headPitch == null ? null : headPitch - thresholds.neckOffset;

    // The arms, which travel the opposite way to the body: forward and down
    // into the load, back and up through the jump. Measured as how far the arm
    // swings BACK between those two moments rather than as where it ends up,
    // because the movement is what the athlete was asked for and because an end
    // position would also depend on how he carries his arms at rest.
    //
    // Signed, so forward has to be known: `armAngle` is positive toward +x and
    // the athlete may face either way, hence the facing sign. On the good take
    // the arm sits 76-103 degrees forward at the bottom and reaches 65-73
    // behind in the air, a travel of 145-172. On the take where the arms were
    // left out of it, -99 to 144.
    const armsSeen =
      median(samples.map((s) => s.visibility[side])) >=
      thresholds.minVisibility;
    const facing = facingSign(samples, side);
    const forwardArm = (sample) =>
      sample.armAngle == null ? null : facing * sample.armAngle[side];
    const armAtBottom = forwardArm(samples[knees.indexOf(Math.min(...knees))]);
    const airArms =
      airborne == null
        ? []
        : airborne.map(forwardArm).filter((v) => Number.isFinite(v));
    const armBackInAir = airArms.length > 0 ? Math.min(...airArms) : null;
    const armSwing =
      !armsSeen || armBackInAir == null || !Number.isFinite(armAtBottom)
        ? null
        : armAtBottom - armBackInAir;

    // How long the ankle stayed extended, which is the closest these landmarks
    // come to air time and is NOT the same measurement. The ankle extends before
    // the foot leaves the floor and relaxes after it lands, so the figure runs
    // long: 0.50-0.60s on the good take where a jump of that depth physically
    // produces nearer 0.40-0.50s. Informational for that reason — a threshold
    // would be judging the measurement's own bias — and never called a height,
    // which hip-centred landmarks cannot give at all.
    const airSeconds = airborne == null ? null : airMs / 1000;

    return [
      {
        id: "jump_depth",
        label: "Load",
        value: deepest,
        unit: "°",
        precision: 0,
        pass: deepest == null ? null : deepest <= thresholds.jumpDepthMax,
      },
      {
        id: "jump_legs",
        label: "Legs",
        value: peakKnee,
        unit: "°",
        precision: 0,
        pass: peakKnee == null ? null : peakKnee >= thresholds.jumpLegsMin,
      },
      {
        // Shoulder-hip-knee: 180 is a straight line from shoulder to knee, and
        // it closes as the athlete pikes. The separate trunk row below reads
        // the trunk against gravity, which is a different question — a body can
        // be upright and still folded at the waist.
        id: "jump_hips",
        label: "Hips",
        value: peakHip,
        unit: "°",
        precision: 0,
        pass: peakHip == null ? null : peakHip >= thresholds.jumpHipsMin,
      },
      {
        id: "jump_trunk",
        label: "Body",
        value: tallest,
        unit: "°",
        precision: 1,
        pass: tallest == null ? null : tallest <= thresholds.jumpTrunkMax,
      },
      {
        // The ankle at its most extended over the flight, in foot lengths. This
        // is the push-off and nothing else, so it reports and never judges: see
        // "The foot in the air is not measurable" above. Not a jump height
        // either.
        id: "jump_toes",
        label: "Push-off",
        value: toes,
        unit: "\u00d7foot",
        precision: 2,
        informational: true,
        note: "takeoff",
        pass: null,
      },
      {
        // Forward at the bottom, back in the air. The arm is the one part of
        // this drill that is NOT asked to extend, so it gets its own row rather
        // than being folded into the body's shape.
        id: "jump_arms",
        label: "Arms",
        value: armSwing,
        unit: "°",
        precision: 0,
        pass: armSwing == null ? null : armSwing >= thresholds.jumpArmSwingMin,
      },
      {
        // Looking at the floor is a fault the athlete named, and this is the
        // signal the swing already judges, on the same athlete's own neutral.
        // It shares `neckOffset` and `neckTolerance` rather than taking a
        // parallel pair: a neck's anatomy does not change between drills, and
        // two knobs for one zero would drift apart.
        //
        // Read as a median over the flight, not a worst frame. Within a single
        // jump the raw figure moves through 40-57 degrees — the head travels,
        // and the face landmarks are a few pixels across at the resolution a
        // phone records — so a worst-frame reading would be noise. The medians
        // sit at -7 to +8 across the reference take against a 15-degree
        // tolerance, which is what makes it judgeable at all.
        id: "jump_head",
        label: "Head",
        value: head,
        unit: "°",
        precision: 0,
        pass: head == null ? null : Math.abs(head) <= thresholds.neckTolerance,
      },
      {
        // Reports without judging, the way the swing's up/down ratio does: the
        // figure is honest about the athlete's effort and dishonest as an
        // absolute, since it measures the ankle rather than the floor. `pass`
        // stays null and `informational` keeps it out of the cues and the
        // tally; `aggregate` is what puts the set's total on the receipt, which
        // is the form the athlete asked for it in.
        id: "jump_air",
        label: "Air",
        value: airSeconds,
        unit: "s",
        precision: 2,
        informational: true,
        aggregate: "sum",
        // Ten steps of the bar at 0.55s each, so a set of ten good jumps fills
        // it on the last one or two: the good take totals 5.8s over eleven reps.
        // A bar that fills on rep three is not a reward.
        bonusStep: 0.55,
        note: "approx",
        pass: null,
      },
    ];
  },
  /** Only the window metrics that mean anything here; see exercises/index.js. */
  metricIds: ["trunk_rock"],
};
